import { ValidatorFn, Validators } from '@angular/forms';
import type { FormField } from '../interfaces/form.interface';
import { InputType } from '../interfaces/input.interface';
import { getRegistryCodeValidationRule } from '../utils/field-pattern-validation.utils';
import { FormCustomValidators } from '../utils/form-custom-validators';

export class ReactiveFormValidatorsFactory<T> {
    private readonly formCustomValidators = new FormCustomValidators();

    public create(field: FormField<T>): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (field.conditions.isRequired && field.name !== 'password') {
            validators.push(Validators.required);
        }

        if (field.input.maxLength !== null && field.input.maxLength !== undefined) {
            validators.push(Validators.maxLength(field.input.maxLength));
        }

        this.appendConfiguredPatternValidator(field, validators);

        if (field.input.type === InputType.TIME) {
            validators.push(this.formCustomValidators.timeValidator);
        }

        if (field.name?.includes('email')) {
            validators.push(Validators.email);
        }

        if (field.name === 'password') {
            validators.push(this.formCustomValidators.createPasswordRequiredValidator());
        }

        if (field.name === 'password_repeat') {
            validators.push(this.formCustomValidators.createRepeatedPasswordValidator('password'));
        }

        if (field.name?.includes('nif') || field.name?.includes('cif')) {
            validators.push(this.formCustomValidators.spanishIdValidator);
        }

        if (typeof field.checkForDuplicates === 'function') {
            validators.push(this.formCustomValidators.createDuplicateValueValidator(field.checkForDuplicates));
        }

        return validators;
    }

    private appendConfiguredPatternValidator(field: FormField<T>, validators: ValidatorFn[]): void {
        // Hidden controls can contain serialized repeatable values separated by semicolons.
        // Their cell-level validation is handled by RepeatableRowFormFactory.
        if (field.input.type === InputType.HIDDEN) {
            return;
        }

        const explicitPattern = field.input.validationConfiguration?.pattern;
        const explicitMessage = field.input.validationConfiguration?.patternMessage;
        const registryRule = getRegistryCodeValidationRule(field.name, field.label);
        const pattern = explicitPattern ?? registryRule?.pattern;
        const message = explicitMessage ?? registryRule?.message;

        if (pattern && message) {
            validators.push(this.formCustomValidators.createPatternMessageValidator(pattern, message));
        }
    }
}
